import { createSlice } from "@reduxjs/toolkit";
import { CommonProps } from "../../types";
import { getQuoteDisplayTotal } from "../../utils/quoteAddOns";

const initialState: CommonProps = {
  addOns: [],
  selectedAddOns: [],
  quoteList: [],
  filterQuoteList: [],
  originalQuoteList: [],
};
const isValuePresent = (value: unknown) =>
  value !== null && value !== undefined && value !== false && value !== "Not Covered";

const commonSlice = createSlice({
  name: "commonSlice",
  initialState,
  reducers: {
    clearCompare(state) {
      state.quoteList = state.quoteList.map((quote) => ({
        ...quote,
        isCompare: false,
      }));
    },
    addQuoteToCompare(state, action) {
      const item = action.payload;
      const key = `${item?.insurer?.name}_${item?.plan?.name}_${item?.plan?.price?.amount}`;

      state.quoteList = state.quoteList.map((quote) => {
        const quoteKey = `${quote?.insurer?.name}_${quote?.plan?.name}_${quote?.plan?.price?.amount}`;

        if (quoteKey === key) {
          return {
            ...quote,
            isCompare: !quote.isCompare, // ✅ toggle flag
          };
        }

        return quote;
      });

      // 🔁 keep filtered list in sync
      state.filterQuoteList = state.filterQuoteList.map((quote) => {
        const quoteKey = `${quote?.insurer?.name}_${quote?.plan?.name}_${quote?.plan?.price?.amount}`;

        if (quoteKey === key) {
          return {
            ...quote,
            isCompare: !quote.isCompare,
          };
        }

        return quote;
      });
    },

    setAddOns(state, action) {
      state.addOns = action.payload;
    },
    setSelectedAddOns(state, action) {
      state.selectedAddOns = action.payload;
    },
    replaceQuoteList(state, action) {
      const nextQuotes = action.payload || [];
      state.quoteList = [...nextQuotes];
      state.filterQuoteList = [...nextQuotes];
      state.originalQuoteList = [...nextQuotes];
    },
    restoreQuoteListState(state, action) {
      const { quoteList, filterQuoteList, originalQuoteList, selectedAddOns } = action.payload;
      state.quoteList = [...(quoteList || [])];
      state.filterQuoteList = [...(filterQuoteList || [])];
      state.originalQuoteList = [...(originalQuoteList || [])];
      state.selectedAddOns = [...(selectedAddOns || [])];
    },
    setQuoteList(state, action) {
      state.quoteList.push(action.payload);
      state.filterQuoteList.push(action.payload);
      state.originalQuoteList.push(action.payload);
    },
    filterByItemLabels(state, action) {
      const selectedLabels: string[] = action.payload;

      if (!selectedLabels || selectedLabels.length === 0) {
        state.selectedAddOns = [];
        state.quoteList = state.filterQuoteList.map((quote) => ({
          ...quote,
          sections: quote.sections?.map((section) => ({
            ...section,
            items: section.items?.map((item) => ({
              ...item,
              is_checked: false,
            })),
          })),
        }));
        return;
      }

      state.selectedAddOns = selectedLabels;

      state.quoteList = state.filterQuoteList
        .filter((quote) =>
          selectedLabels.every((label) =>
            quote.sections?.some((section) =>
              section.items?.some(
                (item) =>
                  item.label === label && isValuePresent(item.amount ?? (item as any).premium)
              )
            )
          )
        )
        .map((quote) => {
          const updatedSections = quote.sections?.map((section) => ({
            ...section,
            items: section.items?.map((item) => {
              const isMatched =
                selectedLabels.includes(item.label) &&
                isValuePresent(item.amount ?? (item as any).premium);

              return {
                ...item,
                is_checked: isMatched, // ✅ flag added here
              };
            }),
          }));

          return {
            ...quote,
            sections: updatedSections,
          };
        });
    },

    clearFilters(state) {
      state.quoteList = [...state.filterQuoteList];
    },

    sortQuotes(state, action) {
      const sortBy: string = action.payload;
      if (sortBy === "clear") {
        state.quoteList = [...state.originalQuoteList];
        state.filterQuoteList = [...state.originalQuoteList];
        return;
      }
      const getSortedList = (list: any[]) => {
        return [...list].sort((a, b) => {
          switch (sortBy) {
            case "sequence-asc":
              return (
                (a.insurer.insurer_sequence ?? Infinity) - (b.insurer.insurer_sequence ?? Infinity)
              );
            case "price-asc":
              return getQuoteDisplayTotal(a) - getQuoteDisplayTotal(b);

            case "price-desc":
              return getQuoteDisplayTotal(b) - getQuoteDisplayTotal(a);

            case "insurer-asc":
              return (a?.insurer?.name || "").localeCompare(b?.insurer?.name || "");

            case "insurer-desc":
              return (b?.insurer?.name || "").localeCompare(a?.insurer?.name || "");

            default:
              return 0;
          }
        });
      };

      state.quoteList = getSortedList(state.quoteList);
      state.filterQuoteList = getSortedList(state.filterQuoteList);
    },
    clearQuoteList(state) {
      state.selectedAddOns = [];
      state.quoteList = [];
      state.filterQuoteList = [];
      state.originalQuoteList = [];
    },
  },
});

export const {
  addQuoteToCompare,
  setAddOns,
  setSelectedAddOns,
  replaceQuoteList,
  restoreQuoteListState,
  filterByItemLabels,
  setQuoteList,
  clearCompare,
  clearFilters,
  clearQuoteList,
  sortQuotes,
} = commonSlice.actions;

export default commonSlice.reducer;
